import * as React from 'react';
import styled from 'styled-components';

import { Markdown } from 'components/common';
import { useModalContext } from 'common/contexts';
import type {
  EventProceduresAPIType,
  EventProcedureStepAPIType,
} from 'security-app/hooks/api/eventProceduresAPI.types';
import { EmptyList, RowTitle, TD } from 'security-app/components/common';

import RowActionMenu from './RowActionMenu';

type Props = {
  eventProcedures: EventProceduresAPIType<EventProcedureStepAPIType[]>[];
};

const MarkdownStyles = styled.div`
  overflow: hidden auto;
  height: 100%;
  padding: 0 8px;

  container-type: inline-size;

  & > div {
    & > h1,
    & > h2,
    & > h3,
    & > h4,
    & > h5,
    & > h6 {
      margin-bottom: 8px;
      font-family: ${({ theme }) => theme.fonts.family.body};
    }

    & > hr {
      margin: 16px 0;
      border: none;
      border-bottom: 1px solid ${({ theme }) => theme.colors.brand.tertiary};
    }

    & ul,
    & ol {
      padding-left: 1.5rem;
      margin: 8px 0;

      & > li {
        padding: 4px 0;
      }
    }

    & ul {
      list-style-type: disc;
    }

    & p {
      white-space: pre-wrap;
      margin: 8px 0;
    }

    & table {
      border-collapse: collapse;
      border-spacing: 0;
      margin: 8px 0;

      & th,
      & td {
        border: 1px solid ${({ theme }) => theme.colors.input.border};
        padding: 4px 8px;
      }
    }
  }
`;

function ListRows({ eventProcedures }: Props) {
  const { setModal, setEntity } = useModalContext();
  const [selectedProcedureId, setSelectedProcedureId] = React.useState(undefined);

  const onClickProcedure = (procedure: EventProceduresAPIType<Array<EventProcedureStepAPIType>>) => {
    setSelectedProcedureId(procedure.id);
    setEntity(procedure);
    setModal('SHOW-EVENT-PROCEDURE-DETAILS');
  };

  React.useEffect(() => {
    const auxEventProcedure = eventProcedures.find(
      (eventProcedure: EventProceduresAPIType<Array<EventProcedureStepAPIType>>) =>
        selectedProcedureId === eventProcedure.id,
    );

    setEntity(auxEventProcedure);
  }, [eventProcedures, setEntity, selectedProcedureId]);

  return eventProcedures.length < 1 ? (
    <EmptyList
      noSearchResultText="No Event Procedures found for the provided criteria"
      noEntitiesExist="Currently there are no Event Procedures to list"
      colSpan={3}
    />
  ) : (
    <>
      {eventProcedures.map((eventProcedure: EventProceduresAPIType<EventProcedureStepAPIType[]>) => (
        <React.Fragment key={`event-procedure-row-${eventProcedure.id}`}>
          <tr>
            <TD>
              <RowTitle onClick={() => onClickProcedure(eventProcedure)}>
                {eventProcedure.title || 'No title set'}
              </RowTitle>
            </TD>
            <TD style={{ height: '80px' }}>
              <MarkdownStyles>
                <Markdown text={eventProcedure.description} />
              </MarkdownStyles>
            </TD>
            <TD>
              <RowActionMenu eventProcedure={eventProcedure} />
            </TD>
          </tr>
        </React.Fragment>
      ))}
    </>
  );
}

export default ListRows;
